import { useEffect, useRef, useState } from "react";

/**
 * Animates a number counting up from 0 to `target` when the element becomes visible.
 * Returns the formatted display string.
 */
export default function useCountUp(target, { duration = 2000, suffix = "", prefix = "" } = {}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            setStarted(true);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, target, duration]);

  // Formatter
  const format = (n) => {
    if (target >= 1_000_000) {
      return (n / 1_000_000).toFixed(n >= target ? 1 : 1).replace(/\.0$/, "") + "M";
    }
    if (target >= 1000) {
      return Math.round(n).toLocaleString();
    }
    return Math.round(n).toString();
  };

  return {
    ref,
    display: `${prefix}${format(value)}${suffix}`,
  };
}
